<script setup lang="ts">
import type { AlgoliaDropdownFiltersProps } from './AlgoliaDropdownFilters.props'
withDefaults(defineProps<AlgoliaDropdownFiltersProps>(), {
  border: false,
  showMore: true,
  defaultOpen: false,
})
const { hideFilter } = await useTemplatePlp()

const isNoRefinement = ref(false)
const mainDiv = ref(null)

onMounted(() => {
  isNoRefinement.value = hideFilter(mainDiv.value)
})

const numbersOfRefinements = (number: Number) => {
  if (number) {
    return `(${number.toString()}) `
  }
  return ``
}

const filteredItems = <Item extends { value: string }>(items: Item[]) => {
  return items.filter(
    item => item.value.split('_')[0] && item.value.split('_')[1]
  )
}
</script>

<template>
  <div v-if="!isNoRefinement" :id="id" ref="mainDiv">
    <AisRefinementList class="mb-2" :attribute="attribute">
      <template
        #default="{
          items,
          refine,
          canToggleShowMore,
          toggleShowMore,
          isShowingMore,
        }"
      >
        <!-- to remove dropdown elements with only 1 facet filter -->
        <div v-if="items.length === 1" class="oneFacetElement"></div>
        <UIDropdown
          v-if="items.length > 1"
          title-wrapper="h3"
          :default-open="
            !!items.find(obj => obj.isRefined === true) || defaultOpen
          "
          :title="title"
          :right-text="
            numbersOfRefinements(
              items.filter(obj => obj.isRefined === true).length
            )
          "
          :custom-title-style="'text-book-5'"
          :class="{ 'border-primitives-grey-100 border-t': border }"
        >
          <AlgoliaSizeFilter
            v-if="attribute === 'size.sizeCode'"
            :refine="refine"
            :attribute="attribute"
            :items="items"
          />
          <div v-else class="mb-6 flex flex-col gap-6 font-light">
            <div>
              <ul
                v-if="attribute === 'MacroColorDesc'"
                class="mb-4 grid grid-cols-2 gap-3"
              >
                <li
                  v-for="item in filteredItems(items)"
                  :key="`${item.value}_${attribute}`"
                  class="relative flex gap-1"
                >
                  <AtomsCheckbox
                    v-model="item.isRefined"
                    @refine="refine(item.value)"
                  >
                    <template #label>
                      <div class="flex items-center gap-3">
                        <div
                          :style="{
                            'background-color': `#${item.value.split('_')[0]}`,
                          }"
                          class="bg-neutral-dark h-4 w-4 border"
                        />
                        <div class="gap-xxs flex">
                          <span>{{ item.label.split('_')[1] }}</span>
                          <span
                            v-if="item?.count"
                            class="text-light-7 text-primitives-grey-200 flex w-fit items-center"
                          >
                            ({{ item.count }})
                          </span>
                        </div>
                      </div>
                    </template>
                  </AtomsCheckbox>
                </li>
              </ul>

              <ul v-else class="mb-4 flex flex-col gap-4">
                <li
                  v-for="item in items"
                  :key="`${item.value}_${attribute}`"
                  class="flex gap-1"
                >
                  <AtomsCheckbox
                    v-model="item.isRefined"
                    @refine="refine(item.value)"
                  >
                    <template #label>
                      <span class="text-book-6"> {{ item.label }}</span>
                      <span
                        v-if="item?.count"
                        class="text-light-7 text-primitives-grey-200 flex w-fit items-center"
                      >
                        ({{ item.count }})
                      </span>
                    </template>
                  </AtomsCheckbox>
                </li>
              </ul>

              <AtomsCta
                v-if="canToggleShowMore"
                anatomy="link"
                @click="toggleShowMore"
              >
                <template #label>
                  {{ !isShowingMore ? 'Show more' : 'Show less' }}
                </template>
              </AtomsCta>
            </div>
          </div>
        </UIDropdown>
      </template>
      <template #showMoreLabel>
        <span>
          <!-- hide default show more -->
        </span>
      </template>
    </AisRefinementList>
  </div>
</template>
